import React, { useCallback, useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import planning from "../puglins/Utils/models/planning";
import notistack from "../puglins/notistack";
import manageClassStorage from "../helper/OnGoingClassManage";
import { announceClassClosed } from "../helper/roomClosed";
import { useFeature } from "../helper/features";
import { bannerOf } from "../helper/upcomingClass";
import { subjectOfItem } from "../helper/subjectName";

// 📅 Bandeau du prochain cours réservé (prof ou élève), sur toutes les pages : salle ouverte
// (« Rejoindre la salle »), prof qui termine son cours précédent (élève : écran d'attente,
// puis annulation possible après 15 min de retard), élève qui attend (prof), « élève absent »
// après 15 min. Relu toutes les 30 s et à chaque événement du cours. Alertes au prof :
// « prochain cours à … », « votre élève est arrivé ». Rien tant que l'interrupteur est coupé.
const POLL_MS = 30 * 1000;
const REFRESH_EVENTS = ["scheduled-class-booked", "scheduled-class-opened", "class-cancelled", "class-no-show", "class-started", "classEnded-student", "class-completed", "class-auto-completed"];

// 🌙 Couleurs propres au bandeau (tour 39) : une carte aux jetons, liseré de la couleur du ton.
const Box = styled.div`
  position: fixed;
  left: 16px;
  bottom: 16px;
  max-width: 360px;
  z-index: 1040;
  &.ndb {
    padding: 12px 16px;
    border-radius: var(--nd-rayon-lg);
    border: 1px solid var(--nd-trait);
    border-left: 4px solid var(--nd-lien);
    background: var(--nd-surface);
    color: var(--nd-texte);
    font-family: var(--nd-police-texte);
    font-size: var(--nd-texte-petit);
    line-height: 1.45;
    box-shadow: var(--nd-ombre-carte);
  }
  &.ndb[data-tone="success"] {
    border-left-color: var(--nd-vert-texte);
  }
  &.ndb[data-tone="warning"] {
    border-left-color: var(--nd-orange-texte);
  }
  &.ndb .ndb-danger {
    color: var(--nd-danger-texte);
    border-color: var(--nd-danger-texte);
  }
`;
const CONFIRM = {
  cancelLate: { text: "upcoming.confirmCancelLate", done: "upcoming.cancelledLate", run: (id) => planning.cancelForTeacherLate(id) },
  markAbsent: { text: "upcoming.confirmMarkAbsent", done: "upcoming.markedAbsent", run: (id) => planning.markStudentAbsent(id) },
};

function UpcomingClassBanner({ authUserData, socket }) {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const enabled = useFeature("planning");
  const [upcoming, setUpcoming] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    planning
      .upcoming()
      .then((res) => {
        setUpcoming(res?.class || null);
        setNow(Date.now());
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!enabled || !authUserData?._id) return undefined;
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [enabled, authUserData?._id, load]);

  useEffect(() => {
    if (!enabled || !socket) return undefined;
    const onNextClass = (d) => notistack.info(t("upcoming.nextClassAlert", { time: d?.time, student: d?.student, subject: subjectOfItem(d, i18n?.language) }));
    const onStudentWaiting = (d) => {
      notistack.warning(t("upcoming.studentWaitingAlert", { student: d?.student, subject: subjectOfItem(d, i18n?.language) }));
      load();
    };
    REFRESH_EVENTS.forEach((e) => socket.on(e, load));
    socket.on("next-class-alert", onNextClass);
    socket.on("student-waiting", onStudentWaiting);
    return () => {
      REFRESH_EVENTS.forEach((e) => socket.off(e, load));
      socket.off("next-class-alert", onNextClass);
      socket.off("student-waiting", onStudentWaiting);
    };
  }, [enabled, socket, load, t, i18n?.language]);

  // Déjà dans la salle de ce cours : pas de « Rejoindre la salle »
  const inRoom = /^\/zoomclasses/i.test(location.pathname) && !!upcoming && String(manageClassStorage.getDetails()?.id) === String(upcoming.class_id);
  const view = enabled ? bannerOf(upcoming, now, { inRoom, language: i18n?.language }) : null;
  if (!view) return null;

  const join = () => {
    manageClassStorage.saveDetails({ id: upcoming.class_id });
    history.push({ pathname: "/ZoomClasses" });
  };
  const confirm = async () => {
    setBusy(true);
    try {
      await CONFIRM[confirming].run(upcoming.class_id);
      notistack.success(t(CONFIRM[confirming].done));
      // Cours clos : sa salle Zoom (si elle est ouverte) se quitte toute seule
      if (confirming === "markAbsent") announceClassClosed(upcoming.class_id);
      setConfirming(null);
      load();
    } catch (err) {
      notistack.error(err?.response?.data?.error || t("upcoming.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Box
        className="ndb"
        data-tone={view.tone}
        role="status"
        data-testid="upcoming-class"
        data-bottom-bar
        // Nombre d'actions : dans la salle au téléphone, un bandeau sans action est masqué (ZoomStyle.css, tour 66)
        data-actions={view.actions.length}
      >
        <div className="fw-semibold">{t(...view.text)}</div>
        {view.detail && <div className="mt-1">{t(...view.detail)}</div>}
        {view.actions.length > 0 && (
          <div className="d-flex flex-wrap gap-2 mt-2">
            {view.actions.includes("join") &&
              (<button type="button" className="nd-btn nd-btn--petit nd-btn--secondaire" onClick={join}>{t("upcoming.join")}</button>)}
            {["cancelLate", "markAbsent"].filter((a) => view.actions.includes(a)).map((a) =>
              <button key={a} type="button" className="nd-btn nd-btn--petit nd-btn--contour ndb-danger" onClick={() => setConfirming(a)}>{t(`upcoming.${a}`)}</button>
            )}
          </div>
        )}
      </Box>
      <Modal show={!!confirming} onHide={() => setConfirming(null)} centered>
        <Modal.Body className="p-4" data-testid="upcoming-confirm">
          {confirming && <p className="fw-semibold">{t(CONFIRM[confirming].text)}</p>}
          <div className="d-flex gap-2 justify-content-end">
            <Button variant="light" disabled={busy} onClick={() => setConfirming(null)}>{t("upcoming.back")}</Button>
            <Button variant="danger" className="fw-semibold" disabled={busy} onClick={confirm}>{t("upcoming.confirm")}</Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default UpcomingClassBanner;
